import React, { useState } from "react";
import { Box } from "@mui/material";
import Section from "../ui/Section";
import { Accent, Display, Label } from "../ui/Type";
import { color, font, motion, type } from "../../design/tokens";

/**
 * FAQ accordion.
 *
 * Built on real <button> elements inside headings, with `aria-expanded` and
 * `aria-controls` wired to the panel — so it's operable by keyboard and
 * announced correctly, which the styled-div version of this pattern never is.
 * Panels stay in the DOM and are hidden with `hidden`, so browser find-in-page
 * and search crawlers still reach the answers.
 *
 * Any page rendering this also emits matching FAQPage structured data. That
 * only holds up because the answers are genuinely on the page — marking up
 * questions that aren't visible is a guideline violation, not a shortcut.
 */
const FAQ = ({
  faqs = [],
  eyebrow = "Common questions",
  title,
  intro,
  tone = "paper",
  idPrefix = "faq",
}) => {
  const [open, setOpen] = useState(0);

  if (!faqs.length) return null;

  const onInk = tone === "ink";
  const rule = onInk ? color.lineOnInk : color.line;
  const heading = onInk ? color.onInk : color.ink;
  const bodyColor = onInk ? color.onInkMute : color.graphite;

  return (
    <Section tone={tone} id="faq">
      <Box
        sx={{
          display: "grid",
          gridTemplateColumns: { xs: "1fr", md: "0.8fr 1.2fr" },
          gap: { xs: 5, md: 10 },
          alignItems: "start",
        }}
      >
        <Box sx={{ position: { md: "sticky" }, top: { md: 140 } }}>
          <Label tone={onInk ? "onInk" : "ink"}>{eyebrow}</Label>
          <Display as="h2" size="md" tone={onInk ? "onInk" : "ink"} sx={{ marginTop: 3 }}>
            {title || (
              <>
                Questions worth <Accent>asking first</Accent>.
              </>
            )}
          </Display>
          {intro ? (
            <Box component="p" sx={{ ...type.body, color: bodyColor, marginTop: 3 }}>
              {intro}
            </Box>
          ) : null}
        </Box>

        <Box sx={{ borderTop: `1px solid ${rule}` }}>
          {faqs.map((faq, index) => {
            const expanded = open === index;
            const panelId = `${idPrefix}-panel-${index}`;
            const buttonId = `${idPrefix}-button-${index}`;

            return (
              <Box key={faq.question} sx={{ borderBottom: `1px solid ${rule}` }}>
                <Box component="h3" sx={{ margin: 0 }}>
                  <Box
                    component="button"
                    type="button"
                    id={buttonId}
                    aria-expanded={expanded}
                    aria-controls={panelId}
                    onClick={() => setOpen(expanded ? -1 : index)}
                    sx={{
                      width: "100%",
                      display: "flex",
                      alignItems: "flex-start",
                      justifyContent: "space-between",
                      gap: 3,
                      textAlign: "left",
                      background: "none",
                      border: "none",
                      cursor: "pointer",
                      padding: 0,
                      paddingBlock: { xs: 2.75, md: 3.25 },
                      fontFamily: font.body,
                      fontWeight: 600,
                      fontSize: { xs: "1rem", md: "1.0625rem" },
                      lineHeight: 1.45,
                      color: expanded ? (onInk ? color.clayLight : color.clay) : heading,
                      transition: `color ${motion.base} ${motion.ease}`,
                      "&:hover": { color: onInk ? color.clayLight : color.clay },
                    }}
                  >
                    {faq.question}
                    <Box
                      component="svg"
                      aria-hidden="true"
                      viewBox="0 0 14 14"
                      sx={{
                        width: 14,
                        height: 14,
                        flexShrink: 0,
                        marginTop: "5px",
                        fill: "none",
                        stroke: "currentColor",
                        strokeWidth: 1.3,
                        transform: expanded ? "rotate(45deg)" : "none",
                        transition: `transform ${motion.base} ${motion.ease}`,
                      }}
                    >
                      <path d="M7 1v12M1 7h12" />
                    </Box>
                  </Box>
                </Box>

                <Box
                  id={panelId}
                  role="region"
                  aria-labelledby={buttonId}
                  hidden={!expanded}
                  sx={{ paddingBottom: expanded ? { xs: 3, md: 3.5 } : 0 }}
                >
                  <Box
                    component="p"
                    sx={{ ...type.body, color: bodyColor, margin: 0, maxWidth: 660 }}
                  >
                    {faq.answer}
                  </Box>
                </Box>
              </Box>
            );
          })}
        </Box>
      </Box>
    </Section>
  );
};

export default FAQ;
